import React, { createContext, useContext, useEffect, useState } from "react";
import api, { saveAuth, clearAuth, getUser, isCookieAuth } from "./api";
import { setLanguage } from "../i18n";

const AuthCtx = createContext(null);

/**
 * Reconcile the client's current UI language with the server's stored preference.
 * Priority:
 *   1. If the user has explicitly picked a language locally (hb_lang_seen==='1')
 *      AND it differs from the server, push UP to server (local wins).
 *   2. Else if the server has a language, adopt it and set locally.
 *   3. Else push the current UI language UP so it's remembered.
 */
function reconcileLanguage(u) {
  const localLang = (typeof localStorage !== "undefined" && localStorage.getItem("hb_lang")) || "en";
  const serverLang = u?.profile?.language_code;
  const userChose = typeof localStorage !== "undefined" && localStorage.getItem("hb_lang_seen") === "1";
  if (userChose && localLang && localLang !== serverLang) {
    api.put("/auth/language", { language: localLang }).catch(() => {});
    setLanguage(localLang);
  } else if (serverLang) {
    setLanguage(serverLang);
  } else {
    api.put("/auth/language", { language: localLang }).catch(() => {});
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getUser());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If we already appear authenticated (cookie) or have a cached user, try /auth/me
    const cachedUser = getUser();
    const shouldCheck = cachedUser || isCookieAuth();
    if (!shouldCheck) return;

    setLoading(true);
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("hb_user", JSON.stringify(r.data));
        reconcileLanguage(r.data);
      })
      .catch(() => clearAuth())
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(email, password) {
    const r = await api.post("/auth/login", { email, password });
    saveAuth(r.data.token, r.data.user);
    setUser(r.data.user);
    reconcileLanguage(r.data.user);
    return r.data.user;
  }

  async function register(payload) {
    const r = await api.post("/auth/register", payload);
    saveAuth(r.data.token, r.data.user);
    setUser(r.data.user);
    reconcileLanguage(r.data.user);
    return r.data.user;
  }

  /** Emergent-managed Google login callback. */
  async function completeGoogleSession(session_id) {
    const r = await api.post("/auth/session-google", { session_id });
    saveAuth(r.data.token, r.data.user);
    setUser(r.data.user);
    reconcileLanguage(r.data.user);
    return r.data.user;
  }

  function logout() {
    clearAuth();
    setUser(null);
  }

  async function refresh() {
    const r = await api.get("/auth/me");
    setUser(r.data);
    localStorage.setItem("hb_user", JSON.stringify(r.data));
    reconcileLanguage(r.data);
    return r.data;
  }

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, completeGoogleSession }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

/** Start the Emergent Google auth redirect. Returns to /auth/callback. */
export function startGoogleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirect = window.location.origin + "/auth/callback";
  window.location.href = "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirect);
}
